import {
  AgeGroup,
  roundOffToSignificantFigures,
  Unit,
} from "../../../constants/exportsCountries"
import { sortNonMandatoryNutrientsData } from "../CanadaLabelPreview/NutritionCanadaHelper"

import {
  convertQuantityToBaseUnit,
  removeEmptyStringValuesFromObject,
} from "../IndiaLabelPreview/NutritionIndiaHelper"
import {
  GCC_DV_VALUES,
  GCC_INTERLEAVED_NUTRIENTS,
  GCC_NO_DV_NUTRIENT_KEYS,
  GCC_NON_MANDATORY_NUTRIENT_SEQUENCE,
  GCC_NUTRITION_KEYS,
  GCCInterLeavedNonMandatoryNutrientData,
  GCCLabelJson,
  GCCMandatoryNutrientObject,
  GCCNutrientData,
  GCCNutritionTableValue,
} from "./GCCTypes"

export function calculateGCCNutritionTableValues(
  label_json: GCCLabelJson,
): GCCNutritionTableValue {
  const {
    serving_size = "",
    serving_size_unit = "g",
    net_quantity = "",
    net_quantity_unit = "g",
    mandatory_nutrient,
    age_group = "Above 18 years",
    non_mandatory_nutrient = [],
    household_quanity,
  } = label_json

  const serving_size_in_base_unit = convertQuantityToBaseUnit(
    serving_size,
    serving_size_unit,
  )

  const rounded_serving_size = roundOffGCCServingSize(serving_size_in_base_unit)

  const serving_per_container = calculateAndRoundOffGCCServingPerContainer(
    serving_size,
    serving_size_unit,
    net_quantity,
    net_quantity_unit,
  )

  const calories = calculateGCCCaloriesAndRoundOff(
    mandatory_nutrient?.[0]?.energy,
    serving_size_in_base_unit,
  )

  const filtered_mandatory_nutrient: GCCMandatoryNutrientObject =
    removeEmptyStringValuesFromObject(mandatory_nutrient[0])

  const mandatory_nutrient_data = getGCCMandatoryNutrientData(
    filtered_mandatory_nutrient,
    serving_size_in_base_unit,
    age_group,
  )

  const non_mandatory_nutrient_data = getGCCNonMandatoryNutrientData(
    non_mandatory_nutrient,
    serving_size_in_base_unit,
    age_group,
  )

  const sorted_non_mandatory_nutrient_data = sortNonMandatoryNutrientsData(
    non_mandatory_nutrient_data,
    GCC_NON_MANDATORY_NUTRIENT_SEQUENCE,
  )

  const interleaved_nutrient_data = getGCCInterLeavedNutrientData(
    serving_size_in_base_unit,
    non_mandatory_nutrient,
  )

  return {
    serving_size: rounded_serving_size,
    serving_size_unit,
    serving_per_container,
    household_quantity: household_quanity,
    calories,
    mandatory_nutrient: mandatory_nutrient_data,
    non_mandatory_nutrient: sorted_non_mandatory_nutrient_data,
    interleaved_nutrient: interleaved_nutrient_data,
  }
}

export function getGCCMandatoryNutrientData(
  filtered_mandatory_nutrient: GCCMandatoryNutrientObject,
  serving_size_in_base_unit: number,
  age_group: AgeGroup,
) {
  const added_sugar_per_serve =
    calculateAndRoundOffGCCMandatoryNutrientPerServe(
      filtered_mandatory_nutrient?.[GCC_NUTRITION_KEYS.ADDED_SUGARS] || "0",
      GCC_NUTRITION_KEYS.ADDED_SUGARS,
      serving_size_in_base_unit,
    )
  const mandatory_nutrient_data = {
    TOTAL_FAT: {
      key: GCC_NUTRITION_KEYS.TOTAL_FAT,
      name: "Total fat / الدهون الكلية",
      unit: "g",
      per_serve: calculateAndRoundOffGCCMandatoryNutrientPerServe(
        filtered_mandatory_nutrient?.[GCC_NUTRITION_KEYS.TOTAL_FAT] || "0",
        GCC_NUTRITION_KEYS.TOTAL_FAT,
        serving_size_in_base_unit,
      ),
      daily_value: calculateAndRoundOffGCCDailyValue(
        filtered_mandatory_nutrient?.[GCC_NUTRITION_KEYS.TOTAL_FAT] || "0",
        GCC_NUTRITION_KEYS.TOTAL_FAT,
        serving_size_in_base_unit,
        age_group,
      ),
    },
    SATURATED_FAT: {
      key: GCC_NUTRITION_KEYS.SATURATED_FAT,
      name: "Saturated fat /الدهون المشبعة",
      unit: "g",
      per_serve: calculateAndRoundOffGCCMandatoryNutrientPerServe(
        filtered_mandatory_nutrient?.[GCC_NUTRITION_KEYS.SATURATED_FAT] || "0",
        GCC_NUTRITION_KEYS.SATURATED_FAT,
        serving_size_in_base_unit,
      ),
      daily_value: calculateAndRoundOffGCCDailyValue(
        filtered_mandatory_nutrient?.[GCC_NUTRITION_KEYS.SATURATED_FAT] || "0",
        GCC_NUTRITION_KEYS.SATURATED_FAT,
        serving_size_in_base_unit,
        age_group,
      ),
    },
    TRANS_FAT: {
      key: GCC_NUTRITION_KEYS.TRANS_FAT,
      name: "Trans fat / الدهون المتحولة",
      unit: "g",
      per_serve: calculateAndRoundOffGCCMandatoryNutrientPerServe(
        filtered_mandatory_nutrient?.[GCC_NUTRITION_KEYS.TRANS_FAT] || "0",
        GCC_NUTRITION_KEYS.TRANS_FAT,
        serving_size_in_base_unit,
      ),
      daily_value: "",
    },

    CHOLESTEROL: {
      key: GCC_NUTRITION_KEYS.CHOLESTEROL,
      name: "Cholesterol / الكوليسترول",
      unit: "mg",
      per_serve: calculateAndRoundOffGCCMandatoryNutrientPerServe(
        filtered_mandatory_nutrient?.[GCC_NUTRITION_KEYS.CHOLESTEROL] || "0",
        GCC_NUTRITION_KEYS.CHOLESTEROL,
        serving_size_in_base_unit,
      ),
      daily_value: calculateAndRoundOffGCCDailyValue(
        filtered_mandatory_nutrient?.[GCC_NUTRITION_KEYS.CHOLESTEROL] || "0",
        GCC_NUTRITION_KEYS.CHOLESTEROL,
        serving_size_in_base_unit,
        age_group,
      ),
    },
    SODIUM: {
      key: GCC_NUTRITION_KEYS.SODIUM,
      name: "Sodium / الصوديوم",
      unit: "mg",
      per_serve: calculateAndRoundOffGCCMandatoryNutrientPerServe(
        filtered_mandatory_nutrient?.[GCC_NUTRITION_KEYS.SODIUM] || "0",
        GCC_NUTRITION_KEYS.SODIUM,
        serving_size_in_base_unit,
      ),
      daily_value: calculateAndRoundOffGCCDailyValue(
        filtered_mandatory_nutrient?.[GCC_NUTRITION_KEYS.SODIUM] || "0",
        GCC_NUTRITION_KEYS.SODIUM,
        serving_size_in_base_unit,
        age_group,
      ),
    },
    TOTAL_CARBOHYDRATES: {
      key: GCC_NUTRITION_KEYS.TOTAL_CARBOHYDRATE,
      name: "Total carbohydrates/ الكربوهيدرات الكلية",
      unit: "g",
      per_serve: calculateAndRoundOffGCCMandatoryNutrientPerServe(
        filtered_mandatory_nutrient?.[GCC_NUTRITION_KEYS.TOTAL_CARBOHYDRATE] ||
          "0",
        GCC_NUTRITION_KEYS.TOTAL_CARBOHYDRATE,
        serving_size_in_base_unit,
      ),
      daily_value: calculateAndRoundOffGCCDailyValue(
        filtered_mandatory_nutrient?.[GCC_NUTRITION_KEYS.TOTAL_CARBOHYDRATE] ||
          "0",
        GCC_NUTRITION_KEYS.TOTAL_CARBOHYDRATE,
        serving_size_in_base_unit,
        age_group,
      ),
    },
    DIETARY_FIBER: {
      key: GCC_NUTRITION_KEYS.DIETARY_FIBRE,
      name: "Dietary fiber / الألياف الغذائية",
      unit: "g",
      per_serve: calculateAndRoundOffGCCMandatoryNutrientPerServe(
        filtered_mandatory_nutrient?.[GCC_NUTRITION_KEYS.DIETARY_FIBRE] || "0",
        GCC_NUTRITION_KEYS.DIETARY_FIBRE,
        serving_size_in_base_unit,
      ),
      daily_value: calculateAndRoundOffGCCDailyValue(
        filtered_mandatory_nutrient?.[GCC_NUTRITION_KEYS.DIETARY_FIBRE] || "0",
        GCC_NUTRITION_KEYS.DIETARY_FIBRE,
        serving_size_in_base_unit,
        age_group,
      ),
    },
    TOTAL_SUGARS: {
      key: GCC_NUTRITION_KEYS.TOTAL_SUGARS,
      name: "Total sugars / السكريات الكلية",
      unit: "g",
      per_serve: calculateAndRoundOffGCCMandatoryNutrientPerServe(
        filtered_mandatory_nutrient?.[GCC_NUTRITION_KEYS.TOTAL_SUGARS] || "0",
        GCC_NUTRITION_KEYS.TOTAL_SUGARS,
        serving_size_in_base_unit,
      ),
      daily_value: "",
    },
    INCLUDES_ADDED_SUGARS: {
      key: GCC_NUTRITION_KEYS.ADDED_SUGARS,
      name:
        `Contains ${added_sugar_per_serve} added sugar / غرام سكر مضاف` +
        " " +
        added_sugar_per_serve +
        " " +
        "يتضمن ",
      unit: "g",
      per_serve: added_sugar_per_serve,
      daily_value: calculateAndRoundOffGCCDailyValue(
        filtered_mandatory_nutrient?.[GCC_NUTRITION_KEYS.ADDED_SUGARS],
        GCC_NUTRITION_KEYS.ADDED_SUGARS,
        serving_size_in_base_unit,
        age_group,
      ),
    },
    PROTEIN: {
      key: GCC_NUTRITION_KEYS.PROTEIN,
      name: "Protein / بروتين",
      unit: "g",
      per_serve: calculateAndRoundOffGCCMandatoryNutrientPerServe(
        filtered_mandatory_nutrient?.[GCC_NUTRITION_KEYS.PROTEIN] || "0",
        GCC_NUTRITION_KEYS.PROTEIN,
        serving_size_in_base_unit,
      ),
      daily_value: "",
    },
  }

  return mandatory_nutrient_data
}

export function getGCCInterLeavedNutrientData(
  serving_size_in_base_unit: number,
  non_mandatory_nutrient: { name: string; quantity: string }[],
) {
  const interleaved_nutrient_map = {
    MONOUNSATURATED_FATTY_ACIDS: {
      key: GCC_NUTRITION_KEYS.MUFA,
      unit: "g",
      name: "Monounsaturated Fatty Acids / الأحماض الدهنية الأحادية غير المشبعة ",
    },
    POLYUNSATURATED_FATTY_ACIDS: {
      key: GCC_NUTRITION_KEYS.PUFA,
      unit: "g",
      name: "Polyunsaturated Fatty Acids / الأحماض الدهنية المتعددة غير المشبعة ",
    },
    POLYOLS: {
      key: GCC_NUTRITION_KEYS.POLYOLS,
      unit: "g",
      name: "Polyols / البوليولات",
    },
    STARCH: {
      key: GCC_NUTRITION_KEYS.STARCH,
      unit: "g",
      name: "Starch / نشا",
    },
  }

  const result: GCCInterLeavedNonMandatoryNutrientData = {}

  for (const [label, data] of Object.entries(interleaved_nutrient_map)) {
    const nutrient = non_mandatory_nutrient.find(
      (item) =>
        item.name === data.key &&
        item.quantity !== "" &&
        item.quantity !== null &&
        item.quantity !== undefined,
    )

    if (nutrient) {
      result[label as keyof GCCInterLeavedNonMandatoryNutrientData] = {
        ...data,
        per_serve:
          calculateAndRoundOffGCCNonMandatoryInterLeavedNutrientPerServe(
            nutrient.quantity,
            data.key,
            serving_size_in_base_unit,
          ),
        daily_value: "",
      }
    }
  }

  return result
}

export function getGCCNonMandatoryNutrientData(
  non_mandatory_nutrient: { name: string; quantity: string }[] = [],
  serving_size_in_base_unit: number,
  age_group: AgeGroup,
): GCCNutrientData[] {
  const filtered_non_mandatory_nutrient = non_mandatory_nutrient?.filter(
    (nutrient) =>
      nutrient.name?.trim() &&
      nutrient.quantity !== undefined &&
      nutrient.quantity !== null &&
      !GCC_INTERLEAVED_NUTRIENTS?.includes(nutrient.name) &&
      nutrient.quantity.trim() !== "",
  )

  return filtered_non_mandatory_nutrient?.map((nutrient) => {
    const nutrient_key = nutrient.name
    const per_100g_value = nutrient.quantity ?? ""

    const nutrient_details =
      GCC_DV_VALUES?.[age_group as keyof typeof GCC_DV_VALUES]?.[nutrient_key]

    return {
      key: nutrient_key,
      unit: nutrient_details?.unit,
      name: nutrient_details?.nutrient_name,
      per_serve: calculateAndRoundOffGCCNonMandatoryNutrientPerServe(
        per_100g_value,
        nutrient_key,
        serving_size_in_base_unit,
      ),
      daily_value: calculateAndRoundOffGCCDailyValue(
        per_100g_value,
        nutrient_key,
        serving_size_in_base_unit,
        age_group,
      ),
    }
  })
}

export function roundOffGCCServingSize(serving_size_in_base_unit: number) {
  // round to one decimal place

  if (
    !isFinite(serving_size_in_base_unit) ||
    serving_size_in_base_unit === null
  ) {
    return ""
  }
  return Math.round(serving_size_in_base_unit * 10) / 10
}

/**
 * Calculate how many servings are in a pack (GCC rules), then round to nearest whole.
 * 4.0  → 4
 * 3.2  → 3
 * 6.5  → 7
 * 9.49 → 9
 * 12.75→ 13
 */
export function calculateAndRoundOffGCCServingPerContainer(
  serving_size: string | null | undefined,
  serving_size_unit: Unit | null | undefined,
  net_quantity: string | null | undefined,
  net_quantity_unit: Unit | null | undefined,
): number | string {
  if (
    !serving_size ||
    !serving_size_unit ||
    !net_quantity ||
    !net_quantity_unit
  ) {
    return ""
  }

  const serving_size_num = parseFloat(serving_size)
  const net_quantity_num = parseFloat(net_quantity)

  if (isNaN(serving_size_num) || isNaN(net_quantity_num)) {
    return ""
  }

  const serving_size_in_base_unit = convertQuantityToBaseUnit(
    serving_size_num,
    serving_size_unit,
  )
  const net_quantity_in_base_unit = convertQuantityToBaseUnit(
    net_quantity_num,
    net_quantity_unit,
  )

  const number_of_servings =
    net_quantity_in_base_unit / serving_size_in_base_unit

  if (!isFinite(number_of_servings)) {
    return ""
  }

  return Math.round(number_of_servings)
}

export function calculateGCCCaloriesAndRoundOff(
  energy_per_100g_value: number | string,
  serving_size_in_base_unit: number,
): number | string {
  const parsed_energy =
    typeof energy_per_100g_value === "string"
      ? parseFloat(energy_per_100g_value)
      : energy_per_100g_value

  // Validate inputs: empty, null, undefined, NaN, Infinity
  if (
    parsed_energy === undefined ||
    serving_size_in_base_unit === undefined ||
    parsed_energy === null ||
    serving_size_in_base_unit === null ||
    isNaN(parsed_energy) ||
    isNaN(serving_size_in_base_unit) ||
    !isFinite(parsed_energy) ||
    !isFinite(serving_size_in_base_unit)
  ) {
    return ""
  }

  // Compute calories per serve
  const calories_per_serve = (parsed_energy * serving_size_in_base_unit) / 100

  if (!isFinite(calories_per_serve) || isNaN(calories_per_serve)) {
    return ""
  }

  //Calorie value per serve is rounded to the nearest 1 kcal/kJ (without decimal point)
  return Math.round(calories_per_serve)
}

export function calculateAndRoundOffGCCMandatoryNutrientPerServe(
  per_100g_value: string,
  nutrient_key: string,
  serving_size_in_base_unit: number,
) {
  const parse_per_100g_value =
    typeof per_100g_value === "string"
      ? parseFloat(per_100g_value)
      : per_100g_value

  if (
    parse_per_100g_value === undefined ||
    serving_size_in_base_unit === undefined ||
    parse_per_100g_value === null ||
    serving_size_in_base_unit === null ||
    isNaN(parse_per_100g_value) ||
    isNaN(serving_size_in_base_unit) ||
    !isFinite(parse_per_100g_value) ||
    !isFinite(serving_size_in_base_unit)
  ) {
    return ""
  }

  const nutrient_per_serve =
    (parse_per_100g_value * serving_size_in_base_unit) / 100

  if (!isFinite(nutrient_per_serve) || isNaN(nutrient_per_serve)) {
    return ""
  }

  if (
    [
      GCC_NUTRITION_KEYS.TOTAL_FAT,

      GCC_NUTRITION_KEYS.TOTAL_CARBOHYDRATE,
      GCC_NUTRITION_KEYS.DIETARY_FIBRE,
      GCC_NUTRITION_KEYS.TOTAL_SUGARS,
      GCC_NUTRITION_KEYS.ADDED_SUGARS,
      GCC_NUTRITION_KEYS.PROTEIN,
    ].includes(nutrient_key)
  ) {
    // if nutrient per serve ≤ 0.5 g/serve, express as 0
    if (nutrient_per_serve <= 0.5) {
      return 0
    }

    // If nutrient per serve < 10 g or > 0.5 g/serve, round off to the nearest 0.1 g
    if (nutrient_per_serve < 10) {
      return Math.round(nutrient_per_serve * 10) / 10
    }

    // If nutrient per serve ≥ 10 g, round off to the nearest whole number (without decimal point)
    return Math.round(nutrient_per_serve)
  }

  if (
    [
      GCC_NUTRITION_KEYS.SATURATED_FAT,
      GCC_NUTRITION_KEYS.CHOLESTEROL,
      GCC_NUTRITION_KEYS.TRANS_FAT,
    ].includes(nutrient_key)
  ) {
    // if nutrient per serve ≤ 0.1 g/serve, express as 0
    if (nutrient_per_serve <= 0.1) {
      return 0
    }

    // If nutrient per serve < 10 g or > 0.1 g/serve, round off to the nearest 0.1 g
    if (nutrient_per_serve < 10) {
      return Math.round(nutrient_per_serve * 10) / 10
    }

    // If nutrient per serve ≥ 10 g, round off to the nearest whole number (without decimal point)
    return Math.round(nutrient_per_serve)
  }

  if (nutrient_key === GCC_NUTRITION_KEYS.SODIUM) {
    // if nutrient per serve ≤ 5 mg/serve, express as 0
    if (nutrient_per_serve <= 5) {
      return 0
    }

    // If nutrient per serve < 1000 mg or > 5 mg/serve, round off to the nearest 10 g
    if (nutrient_per_serve < 1000) {
      return Math.round(nutrient_per_serve / 10) * 10
    }

    // If Sodium ≥ 1000 mg/serve → round to the nearest 100 mg (no decimals)
    return Math.round(nutrient_per_serve / 100) * 100
  }

  return Math.round(nutrient_per_serve * 10) / 10
}

/**
 * This function does not calculate MUFA, PUFA,polyols, and starch per serve.
 * Use calculateAndRoundOffGCCNonMandatoryInterLeavedNutrientPerServe() to retrieve the data for the above nutrients.
 */
export function calculateAndRoundOffGCCNonMandatoryNutrientPerServe(
  per_100g_value: string,
  nutrient_key: string,
  serving_size_in_base_unit: number,
): number | string {
  if (GCC_INTERLEAVED_NUTRIENTS.includes(nutrient_key)) {
    return ""
  }
  const parse_per_100g_value =
    typeof per_100g_value === "string"
      ? parseFloat(per_100g_value)
      : per_100g_value

  if (
    parse_per_100g_value === undefined ||
    serving_size_in_base_unit === undefined ||
    parse_per_100g_value === null ||
    serving_size_in_base_unit === null ||
    isNaN(parse_per_100g_value) ||
    isNaN(serving_size_in_base_unit) ||
    !isFinite(parse_per_100g_value) ||
    !isFinite(serving_size_in_base_unit)
  ) {
    return ""
  }

  const nutrient_per_serve =
    (parse_per_100g_value * serving_size_in_base_unit) / 100

  if (!isFinite(nutrient_per_serve) || isNaN(nutrient_per_serve)) {
    return ""
  }

  if (
    [
      GCC_NUTRITION_KEYS.VITAMIN_A,
      GCC_NUTRITION_KEYS.FOLIC_ACID,
      GCC_NUTRITION_KEYS.CHLORIDE,
      GCC_NUTRITION_KEYS.CALCIUM,
      GCC_NUTRITION_KEYS.PHOSPHORUS,
      GCC_NUTRITION_KEYS.MAGNESIUM,
      GCC_NUTRITION_KEYS.IODINE,
      GCC_NUTRITION_KEYS.POTASSIUM,
    ].includes(nutrient_key)
  ) {
    return roundOffToSignificantFigures(nutrient_per_serve, 3)
  }

  if (
    [GCC_NUTRITION_KEYS.POLYOLS, GCC_NUTRITION_KEYS.STARCH].includes(
      nutrient_key,
    )
  ) {
    // if nutrient per serve ≤ 0.5 g/serve, express as 0
    if (nutrient_per_serve <= 0.5) {
      return 0
    }

    // If nutrient per serve < 10 g or > 0.5 g/serve, round off to the nearest 0.1 g
    if (nutrient_per_serve < 10) {
      return Math.round(nutrient_per_serve * 10) / 10
    }

    // If nutrient per serve ≥ 10 g, round off to the nearest whole number (without decimal point)
    return Math.round(nutrient_per_serve)
  }

  return roundOffToSignificantFigures(nutrient_per_serve, 2)
}

/**
 * This function calculates MUFA ,PUFA,polyols, and starch per serve.
 * It rounds off Per Serve values based on the GCC guidelines.
 */
export function calculateAndRoundOffGCCNonMandatoryInterLeavedNutrientPerServe(
  per_100g_value: string,
  nutrient_key: string,
  serving_size_in_base_unit: number,
) {
  const parse_per_100g_value =
    typeof per_100g_value === "string"
      ? parseFloat(per_100g_value)
      : per_100g_value

  if (
    parse_per_100g_value === undefined ||
    serving_size_in_base_unit === undefined ||
    parse_per_100g_value === null ||
    serving_size_in_base_unit === null ||
    isNaN(parse_per_100g_value) ||
    isNaN(serving_size_in_base_unit) ||
    !isFinite(parse_per_100g_value) ||
    !isFinite(serving_size_in_base_unit)
  ) {
    return ""
  }

  const nutrient_per_serve =
    (parse_per_100g_value * serving_size_in_base_unit) / 100

  if (!isFinite(nutrient_per_serve) || isNaN(nutrient_per_serve)) {
    return ""
  }

  if (
    [GCC_NUTRITION_KEYS.MUFA, GCC_NUTRITION_KEYS.PUFA].includes(nutrient_key)
  ) {
    // if nutrient per serve ≤ 0.1 g/serve, express as 0
    if (nutrient_per_serve <= 0.1) {
      return 0
    }

    // If nutrient per serve < 10 g or > 0.1 g/serve, round off to the nearest 0.1 g
    if (nutrient_per_serve < 10) {
      return Math.round(nutrient_per_serve * 10) / 10
    }

    // If nutrient per serve ≥ 10 g, round off to the nearest whole number (without decimal point)
    return Math.round(nutrient_per_serve)
  }

  if (
    [GCC_NUTRITION_KEYS.POLYOLS, GCC_NUTRITION_KEYS.STARCH].includes(
      nutrient_key,
    )
  ) {
    // if nutrient per serve ≤ 0.5 g/serve, express as 0
    if (nutrient_per_serve <= 0.5) {
      return 0
    }

    // If nutrient per serve < 10 g or > 0.5 g/serve, round off to the nearest 0.1 g
    if (nutrient_per_serve < 10) {
      return Math.round(nutrient_per_serve * 10) / 10
    }

    // If nutrient per serve ≥ 10 g, round off to the nearest whole number (without decimal point)
    return Math.round(nutrient_per_serve)
  }

  return Math.round(nutrient_per_serve * 10) / 10
}

export function calculateAndRoundOffGCCDailyValue(
  per_100g: string,
  nutrient_key: string,
  serving_size_in_base_unit: number,
  age_group: AgeGroup,
) {
  if (GCC_NO_DV_NUTRIENT_KEYS.includes(nutrient_key)) {
    return ""
  }

  if (
    per_100g === null ||
    per_100g === undefined ||
    per_100g === "" ||
    serving_size_in_base_unit === null ||
    serving_size_in_base_unit === undefined
  ) {
    return ""
  }

  const parse_per_100g_value =
    typeof per_100g === "string" ? parseFloat(per_100g) : per_100g

  serving_size_in_base_unit =
    typeof serving_size_in_base_unit === "string"
      ? parseFloat(serving_size_in_base_unit)
      : serving_size_in_base_unit

  const dv_value: number | null =
    GCC_DV_VALUES?.[age_group as keyof typeof GCC_DV_VALUES]?.[nutrient_key]
      ?.daily_value

  if (!dv_value || isNaN(dv_value)) {
    return ""
  }

  const percentDV =
    parse_per_100g_value * (serving_size_in_base_unit / dv_value)

  if (isNaN(percentDV) || !isFinite(percentDV)) {
    return ""
  }

  return Math.round(percentDV)
}
